// src/pages/admin/ADPNetsuiteSubmitFormPreview.jsx
import { useState, useEffect, useMemo } from "react";
import {
  useNetsuiteAccounts,
  useNetsuiteSubsidiaries,
  useNetsuiteAccountingPeriods,
} from "@api/hooks";
import { ADP_CO_ACCOUNT_MAP } from "@utils/contants";
import { torontoToday, torontoDateParts } from "@utils";
import { PreviewField, PreviewInput, fmtLineAmount, computeLineBalance } from "@common";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Find which key in a CSV row holds the account number. */
function findAccountCol(row) {
  const candidates = [
    "Account",
    "Account Number",
    "GL Account",
    "Account #",
    "Acct",
    "Acct #",
    "Account No",
  ];
  const keys = Object.keys(row);
  return (
    keys.find((k) => candidates.some((c) => k.trim().toLowerCase() === c.toLowerCase())) ??
    keys.find((k) => k.toLowerCase().includes("account")) ??
    null
  );
}

/**
 * Given a raw account value from the CSV and the group's ADP CO:
 * - If the value is numeric → use as-is.
 * - If it contains non-numeric chars (placeholder like "10111XX") → look up ADP CO map.
 */
function resolveAcctNumber(rawValue, adpCo) {
  const str = String(rawValue ?? "").trim();
  if (str && /^\d+[-]?$/.test(str)) return str;
  return ADP_CO_ACCOUNT_MAP[adpCo]?.acctNumber ?? null;
}

/** Find a column whose name (case-insensitive, trimmed) matches one of `exact`, then any
 *  column whose name `includes` one of the substrings. Returns null if nothing matches. */
function findCol(row, exact = [], includes = []) {
  const keys = Object.keys(row || {});
  const norm = (s) => String(s).trim().toLowerCase();
  const exactSet = new Set(exact.map(norm));
  const byExact = keys.find((k) => exactSet.has(norm(k)));
  if (byExact) return byExact;
  const incs = includes.map(norm);
  return keys.find((k) => incs.some((c) => norm(k).includes(c))) ?? null;
}

function findDebitCol(row) {
  return findCol(row, ["Debit", "Debit Amount", "DR", "Dr Amount", "Amount Debit"], ["debit"]);
}

function findCreditCol(row) {
  return findCol(row, ["Credit", "Credit Amount", "CR", "Cr Amount", "Amount Credit"], ["credit"]);
}

function findMemoCol(row) {
  return findCol(
    row,
    [
      "Memo",
      "Description",
      "Line Memo",
      "Employee Name",
      "Name",
      "Employee",
      "Full Name",
      "Last Name",
      "Employee ID",
    ],
    ["memo", "description", "name"],
  );
}

function findSubsidiaryCol(row) {
  return findCol(
    row,
    ["Subsidiary", "Subsidiary Name", "Subsidiary ID", "NS Subsidiary"],
    ["subsidiary"],
  );
}

/** Coerce a CSV money string ("$1,234.56", "(123.45)", "") to a number or null. */
function parseMoney(raw) {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).trim();
  if (!s) return null;
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    neg = true;
    s = s.slice(1, -1);
  }
  s = s.replace(/[$,\s]/g, "");
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

/** Today as YYYY-MM-DD */
function getToday() {
  return torontoToday();
}

/**
 * Default memo: "Practice Payroll ending MM/DD/YYYY - <ADP CO> - Smiles Analytics"
 *
 * If `endDate` (YYYY-MM-DD) is provided, that date is used directly.
 * Otherwise falls back to deriving the end date from today:
 *  - today ≤ 15 → 15th of current month
 *  - today > 15 → last day of current month
 */
export function getDefaultAdpMemo(adpCo, endDate) {
  let mm, dd, year;
  if (endDate) {
    const [y, mo, d] = endDate.split("-");
    mm = mo;
    dd = d;
    year = y;
  } else {
    const today = torontoDateParts();
    const day = today.day;
    year = String(today.year);
    const monthIdx = today.month - 1;
    mm = String(monthIdx + 1).padStart(2, "0");
    const endingDay = day <= 15 ? 15 : new Date(today.year, monthIdx + 1, 0).getDate();
    dd = String(endingDay).padStart(2, "0");
  }
  const base = `Practice Payroll ending ${mm}/${dd}/${year}`;
  const co = adpCo ? String(adpCo).trim() : "";
  return co ? `${base} - ${co} - Smiles Analytics` : `${base} - Smiles Analytics`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Preview + edit form for an ADP NetSuite journal entry.
 *
 * Props:
 *   preview           – { adpCo: string, rows: object[] }
 *   onPayloadChange   – called with the full payload whenever editable state
 *                       changes; pass null when account data isn't loaded yet.
 */
export default function ADPNetsuiteSubmitFormPreview({
  preview,
  onPayloadChange,
  periodName,
  endDate,
}) {
  const { adpCo, rows = [] } = preview ?? {};

  // ── Posting period resolution ────────────────────────────────────────────
  const accountingPeriodQuery = useNetsuiteAccountingPeriods(
    { periodname: periodName },
    { enabled: !!periodName },
  );
  const postingPeriodId = useMemo(() => {
    if (!periodName) return null;
    const data = accountingPeriodQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const match = list.find(
      (p) => String(p?.periodname ?? "").toLowerCase() === periodName.toLowerCase(),
    );
    return match ? String(match.id) : null;
  }, [periodName, accountingPeriodQuery.data]);

  // ── Per-row resolved account numbers ────────────────────────────────────
  // Find the relevant CSV columns once from the first row.
  const accountCol = useMemo(() => (rows.length > 0 ? findAccountCol(rows[0]) : null), [rows]);
  const debitCol = useMemo(() => (rows.length > 0 ? findDebitCol(rows[0]) : null), [rows]);
  const creditCol = useMemo(() => (rows.length > 0 ? findCreditCol(rows[0]) : null), [rows]);
  const memoCol = useMemo(() => (rows.length > 0 ? findMemoCol(rows[0]) : null), [rows]);
  const subsidiaryCol = useMemo(
    () => (rows.length > 0 ? findSubsidiaryCol(rows[0]) : null),
    [rows],
  );

  // Subsidiary value pulled from the CSV (first non-empty across rows).
  // Can be either a numeric id or a free-text name — handled below.
  const csvSubsidiaryValue = useMemo(() => {
    if (!subsidiaryCol) return null;
    for (const row of rows) {
      const raw = row?.[subsidiaryCol];
      if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
        return String(raw).trim();
      }
    }
    return null;
  }, [rows, subsidiaryCol]);

  const csvSubsidiaryIsNumeric = !!csvSubsidiaryValue && /^\d+$/.test(csvSubsidiaryValue);
  const csvSubsidiaryIsName = !!csvSubsidiaryValue && !csvSubsidiaryIsNumeric;

  // For each row, resolve its account number (replacing placeholders via ADP CO map).
  const rowAcctNumbers = useMemo(
    () => rows.map((row) => resolveAcctNumber(accountCol ? row[accountCol] : null, adpCo)),
    [rows, accountCol, adpCo],
  );

  // Unique, non-null account numbers to fetch in one call.
  const uniqueAcctNumbers = useMemo(
    () => [...new Set(rowAcctNumbers.filter(Boolean))],
    [rowAcctNumbers],
  );

  // The group's "clearing" account (10111XX for this ADP CO) — used for validation warning.
  const clearingAcctNumber = ADP_CO_ACCOUNT_MAP[adpCo]?.acctNumber ?? null;

  // ── Fetch all account records at once ────────────────────────────────────
  const accountQuery = useNetsuiteAccounts(
    { acctnumbers: uniqueAcctNumbers },
    { enabled: uniqueAcctNumbers.length > 0 },
  );

  // Build acctNumber → account-record map from the response.
  const accountByNumber = useMemo(() => {
    const data = accountQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const map = {};
    for (const a of list) {
      // The API may return acctNumber / accountnumber / number etc.
      const num = a?.acctNumber ?? a?.accountnumber ?? a?.number ?? a?.acctnumber ?? null;
      if (num) map[String(num)] = a;
    }
    return map;
  }, [accountQuery.data]);

  const clearingAccount = useMemo(
    () => (clearingAcctNumber ? (accountByNumber[clearingAcctNumber] ?? null) : null),
    [accountByNumber, clearingAcctNumber],
  );

  // ── Subsidiary resolution ────────────────────────────────────────────────
  //
  // Priority:
  //   1. CSV Subsidiary column with a value
  //      a. numeric → use directly as id
  //      b. text   → look up by name from the subsidiary list
  //   2. ADP_CO_ACCOUNT_MAP hardcoded subsidiaryId / subsidiaryName (no API needed)
  const mapEntry = ADP_CO_ACCOUNT_MAP[adpCo] ?? null;

  // Fetch subsidiary only when the CSV overrides with a name or numeric id.
  const csvSubsidiaryQuery = useNetsuiteSubsidiaries(
    csvSubsidiaryIsNumeric ? { id: csvSubsidiaryValue } : {},
    { enabled: !!csvSubsidiaryValue },
  );

  const csvResolvedSubsidiaryId = useMemo(() => {
    if (!csvSubsidiaryValue) return null;
    if (csvSubsidiaryIsNumeric) return csvSubsidiaryValue;
    const data = csvSubsidiaryQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const wanted = csvSubsidiaryValue.toLowerCase();
    const match = list.find((s) => {
      const name = s?.name ?? s?.fullname ?? s?.fullName ?? "";
      return String(name).trim().toLowerCase() === wanted;
    });
    return match ? String(match.id ?? match.ID ?? "") : null;
  }, [csvSubsidiaryValue, csvSubsidiaryIsNumeric, csvSubsidiaryQuery.data]);

  const subsidiaryId = useMemo(
    () => csvResolvedSubsidiaryId ?? (mapEntry ? String(mapEntry.subsidiaryId) : null),
    [csvResolvedSubsidiaryId, mapEntry],
  );

  // Only hit the API when the CSV provides a subsidiary override we can't resolve locally.
  const subsidiaryQuery = useNetsuiteSubsidiaries(
    { id: subsidiaryId },
    { enabled: !!csvSubsidiaryValue && !!subsidiaryId && !csvSubsidiaryIsName },
  );

  const subsidiaryName = useMemo(() => {
    // Fast path: CSV has no override → use hardcoded name from the map.
    if (!csvSubsidiaryValue && mapEntry?.subsidiaryName) return mapEntry.subsidiaryName;
    const sourceData = csvSubsidiaryIsName ? csvSubsidiaryQuery.data : subsidiaryQuery.data;
    const list =
      sourceData?.items ?? sourceData?.results ?? (Array.isArray(sourceData) ? sourceData : []);
    const match = list.find((s) => String(s?.id ?? s?.ID) === String(subsidiaryId));
    return match?.name ?? match?.fullname ?? match?.fullName ?? subsidiaryId ?? "";
  }, [
    csvSubsidiaryValue,
    mapEntry,
    csvSubsidiaryIsName,
    csvSubsidiaryQuery.data,
    subsidiaryQuery.data,
    subsidiaryId,
  ]);

  // ── Editable header state ────────────────────────────────────────────────
  const [trandate, setTrandate] = useState(getToday);
  const [memo, setMemo] = useState(() => getDefaultAdpMemo(adpCo, endDate));

  // ── Line items (one per CSV row) ─────────────────────────────────────────
  // Prefill debit / credit / memo from the matching CSV columns of each row.
  const [items, setItems] = useState(() =>
    rows.map((row) => {
      const debit = debitCol ? parseMoney(row[debitCol]) : null;
      const credit = creditCol ? parseMoney(row[creditCol]) : null;
      const memo = memoCol ? String(row[memoCol] ?? "") : "";
      return { debit, credit, memo };
    }),
  );

  function updateItem(idx, field, value) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  }

  // ── Totals ───────────────────────────────────────────────────────────────
  const { totalDebit, totalCredit, balanced } = computeLineBalance(items);

  // ── Account validation ───────────────────────────────────────────────────
  // After accounts finish loading, find every row whose account number either
  // didn't come from the CSV or couldn't be matched in NetSuite.
  const missingAccountRows = useMemo(() => {
    if (accountQuery.isLoading) return [];
    return rowAcctNumbers
      .map((acctNum, idx) => ({ idx, acctNum }))
      .filter(({ acctNum }) => !acctNum || !accountByNumber[acctNum]);
  }, [accountQuery.isLoading, rowAcctNumbers, accountByNumber]);

  const hasMissingAccounts = missingAccountRows.length > 0;

  // ── Bubble payload to parent ─────────────────────────────────────────────
  useEffect(() => {
    if (
      !subsidiaryId ||
      accountQuery.isLoading ||
      hasMissingAccounts ||
      (periodName && !postingPeriodId)
    ) {
      onPayloadChange?.(null);
      return;
    }
    const payload = {
      approved: false,
      memo,
      subsidiary: { id: subsidiaryId },
      trandate,
      ...(postingPeriodId ? { postingperiod: { id: postingPeriodId } } : {}),
      line: {
        items: items.map((it, idx) => {
          const acctNum = rowAcctNumbers[idx];
          const acctRecord = acctNum ? accountByNumber[acctNum] : null;
          const acctId = acctRecord?.id ?? acctRecord?.ID ?? null;
          return {
            account: acctId ? { id: String(acctId) } : undefined,
            debit: it.debit !== null && it.debit !== "" ? Number(it.debit) : null,
            credit: it.credit !== null && it.credit !== "" ? Number(it.credit) : null,
            memo: it.memo ?? "",
          };
        }),
      },
    };
    onPayloadChange?.(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memo,
    trandate,
    items,
    subsidiaryId,
    accountByNumber,
    rowAcctNumbers,
    postingPeriodId,
    hasMissingAccounts,
  ]);

  if (!preview) return null;

  const accountsLoading = accountQuery.isLoading;
  const ready = !!subsidiaryId && !accountsLoading;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Warnings */}
      {!accountsLoading && !clearingAccount && clearingAcctNumber && (
        <div className="rounded-md bg-palette-yellow-soft border border-palette-yellow-border px-3 py-2 text-sm text-palette-yellow-text">
          Clearing account <strong>{clearingAcctNumber}</strong> ({adpCo}) not found in NetSuite —
          subsidiary cannot be resolved.
        </div>
      )}
      {clearingAccount && !subsidiaryId && (
        <div className="rounded-md bg-palette-yellow-soft border border-palette-yellow-border px-3 py-2 text-sm text-palette-yellow-text">
          Could not derive subsidiary from account <strong>{clearingAcctNumber}</strong>. Journal
          entry cannot be submitted until resolved.
        </div>
      )}
      {periodName && !accountingPeriodQuery.isLoading && !postingPeriodId && (
        <div className="rounded-md bg-palette-yellow-soft border border-palette-yellow-border px-3 py-2 text-sm text-palette-yellow-text">
          Accounting period <strong>{periodName}</strong> not found in NetSuite — posting period
          cannot be set.
        </div>
      )}
      {!accountsLoading && hasMissingAccounts && (
        <div className="rounded-md bg-palette-red-soft border border-palette-red-border px-3 py-2 text-sm text-palette-red-text">
          <p className="font-semibold mb-1">
            Cannot create journal entry — {missingAccountRows.length} line
            {missingAccountRows.length === 1 ? "" : "s"} have unresolved accounts:
          </p>
          <ul className="list-disc list-inside space-y-0.5">
            {[...new Set(missingAccountRows.map((r) => r.acctNum ?? "(no account number)"))].map(
              (acct) => (
                <li key={acct} className="text-palette-red-heading">
                  <strong>{acct}</strong> — not found in NetSuite
                </li>
              ),
            )}
          </ul>
        </div>
      )}

      {/* Header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-palette-gray-divider bg-palette-gray-soft p-3">
        <PreviewField label="ADP CO" value={adpCo} />
        <PreviewField label="Posting Period">
          {periodName ? (
            accountingPeriodQuery.isLoading ? (
              <span className="text-sm text-palette-gray-placeholder italic">Loading…</span>
            ) : postingPeriodId ? (
              <span className="text-sm font-medium text-palette-gray-text">
                {periodName}{" "}
                <span className="text-palette-gray-placeholder text-xs">
                  (id: {postingPeriodId})
                </span>
              </span>
            ) : (
              <span className="text-sm text-palette-yellow-text font-medium">Not found</span>
            )
          ) : (
            <span className="text-sm text-palette-gray-placeholder italic">No period selected</span>
          )}
        </PreviewField>
        <PreviewField label="Subsidiary">
          {subsidiaryQuery.isLoading || csvSubsidiaryQuery.isLoading ? (
            <span className="text-sm text-palette-gray-placeholder italic">Loading…</span>
          ) : (
            <span className="text-sm font-medium text-palette-gray-text">
              {subsidiaryName || "—"}
            </span>
          )}
        </PreviewField>

        <PreviewField label="Transaction Date">
          <PreviewInput
            type="date"
            value={trandate}
            onChange={(e) => setTrandate(e.target.value)}
            className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
          />
        </PreviewField>

        <PreviewField label="Row Count" value={rows.length} />

        <div className="sm:col-span-2">
          <PreviewField label="Memo">
            <PreviewInput
              type="text"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="w-full text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
            />
          </PreviewField>
        </div>
      </div>

      {/* Line items */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-palette-gray-heading">
            Line Items ({items.length})
            {accountsLoading && (
              <span className="ml-2 text-xs text-palette-gray-placeholder font-normal italic">
                Loading accounts…
              </span>
            )}
          </span>
          <span
            className={`text-xs font-semibold ${balanced ? "text-palette-emerald-hover" : "text-palette-red-heading"}`}
          >
            {balanced ? "✓ Balanced" : "✗ Not balanced"}
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-palette-gray-divider">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-palette-gray-subtle text-palette-gray-heading z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Account</th>
                <th className="text-right px-3 py-2 font-semibold">Debit</th>
                <th className="text-right px-3 py-2 font-semibold">Credit</th>
                <th className="text-left px-3 py-2 font-semibold">Memo</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-palette-gray-placeholder">
                    No rows in this group.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const acctNum = rowAcctNumbers[idx];
                  const acctRecord = acctNum ? accountByNumber[acctNum] : null;
                  const acctMissing = !accountsLoading && (!acctNum || !acctRecord);
                  const acctName =
                    acctRecord?.displaynamewithhierarchy ??
                    acctRecord?.fullname ??
                    acctRecord?.name ??
                    acctNum ??
                    "—";

                  return (
                    <tr
                      key={idx}
                      className={`border-t border-palette-gray-subtle ${acctMissing ? "bg-palette-red-soft" : idx % 2 === 0 ? "bg-white" : "bg-palette-gray-soft"}`}
                    >
                      {/* Account */}
                      <td className="px-3 py-2 align-top">
                        {accountsLoading ? (
                          <span className="text-palette-gray-placeholder italic text-xs">
                            Loading…
                          </span>
                        ) : acctMissing ? (
                          <span className="text-palette-red-badge text-xs font-semibold">
                            {acctNum ?? "—"} · not found
                          </span>
                        ) : (
                          <span
                            className="text-palette-gray-text text-xs leading-snug block break-words"
                            title={acctName}
                          >
                            {acctName}
                          </span>
                        )}
                      </td>

                      {/* Debit */}
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={it.debit ?? ""}
                          onChange={(e) =>
                            updateItem(idx, "debit", e.target.value === "" ? null : e.target.value)
                          }
                          className="w-28 text-right text-sm border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                        />
                      </td>

                      {/* Credit */}
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={it.credit ?? ""}
                          onChange={(e) =>
                            updateItem(idx, "credit", e.target.value === "" ? null : e.target.value)
                          }
                          className="w-28 text-right text-sm border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                        />
                      </td>

                      {/* Memo */}
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={it.memo ?? ""}
                          onChange={(e) => updateItem(idx, "memo", e.target.value)}
                          className="w-full min-w-[140px] text-sm border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            <tfoot className="bg-palette-gray-soft border-t-2 border-palette-gray-divider sticky bottom-0">
              <tr>
                <td className="px-3 py-2 font-semibold text-palette-gray-heading">Totals</td>
                <td className="px-3 py-2 text-right font-semibold">{fmtLineAmount(totalDebit)}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmtLineAmount(totalCredit)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {!ready ? (
        <p className="text-xs text-palette-red-label">
          Cannot submit — waiting for account/subsidiary data to load.
        </p>
      ) : (
        <p className="text-xs text-palette-gray-placeholder">
          This will create a new Journal Entry in NetSuite. Review all values before confirming.
        </p>
      )}
    </div>
  );
}
